
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { ScrollText, Book, Users, Heart, Star, Shield } from 'lucide-react';
import Logo from './logo';
import SearchBar from './SearchBar';

const NamedByGod = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-4 flex items-center justify-center gap-3">
            <Logo width="50" height="50" className="inline-block" />
            <span>Named by <strong>God</strong></span>
          </h1>
          <p className="text-xl text-slate-300 mb-8">Discovering the Divine Purpose in Biblical Names</p>
          <div className="text-slate-300 italic text-lg">
            "Do not fear, for I have redeemed you; I have summoned you by name; you are mine."
            <span className="block mt-2">— Isaiah 43:1</span>
          </div>

          <div className="mt-10">
            <SearchBar />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        {/* Introduction */}
        <Card className="bg-white shadow-lg">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-semibold mb-4 text-slate-900">The Power of Names</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              In ancient times, names held power beyond mere identification. According to biblical tradition, to name something meant to hold power over it, creating a special bond between the namer and the named. This profound connection was especially significant in divine-human relationships, where names often marked transformative encounters with God.
            </p>
          </CardContent>
        </Card>

        {/* Biblical Statistics */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-white">
            <CardHeader>
              <ScrollText className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle className="text-lg">Old Testament</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600 mb-2">2,670</p>
              <p className="text-slate-600">Hebrew proper names preserved in biblical tradition</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardHeader>
              <Book className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle className="text-lg">New Testament</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600 mb-2">580</p>
              <p className="text-slate-600">Greek proper names carrying spiritual significance</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardHeader>
              <Users className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle className="text-lg">Shared Heritage</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-600 mb-2">165</p>
              <p className="text-slate-600">Names common to both Testaments, bridging traditions</p>
            </CardContent>
          </Card>
        </div>

        {/* Key Concepts */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-white">
            <CardHeader>
              <Heart className="h-8 w-8 text-rose-600 mb-2" />
              <CardTitle className="text-lg">Soul Connection</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">According to the Talmud, a person's name reflects their inner essence and character, potentially influencing their destiny and life path.</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardHeader>
              <Star className="h-8 w-8 text-amber-600 mb-2" />
              <CardTitle className="text-lg">Divine Transformation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">Biblical name changes often marked profound spiritual transformations, as with Abram becoming Abraham and Jacob becoming Israel.</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardHeader>
              <Shield className="h-8 w-8 text-emerald-600 mb-2" />
              <CardTitle className="text-lg">Cultural Heritage</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">Names connect individuals to their spiritual and cultural heritage, carrying forward ancient wisdom and divine purpose.</p>
            </CardContent>
          </Card>
        </div>

        {/* Extended Content */}
        <div className="space-y-8">
          <Card className="bg-white">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">Names in Biblical Tradition</h2>
              <div className="space-y-4 text-slate-700">
                <p>
                  In the Hebrew Bible, names were often chosen based on their meaning or significance, reflecting religious beliefs and cultural traditions. Names were closely tied to personal identity and believed to carry specific power or significance. For example, in the story of Jacob and Esau, Jacob's transformation into Israel symbolizes his new identity as the father of the twelve tribes.
                </p>
                <p>
                  The Synoptic Gospels place great importance on names, using them to identify characters and reflect deeper religious themes. These names serve multiple purposes, creating a sense of familiarity and continuity for readers while also conveying social and spiritual significance.
                </p>
                <p>
                  In ancient mythologies and religions, names held even greater power. Gods were often referred to using titles or attributes rather than their direct names, as speaking a deity's name was considered an act of special devotion or connection. This practice reflected the belief that names carried inherent power and could establish profound spiritual bonds.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4 text-slate-900">Modern Significance</h2>
              <div className="space-y-4 text-slate-700">
                <p>
                  Today, while names are often chosen for their sound or family connections, they continue to carry deep meaning for many communities. Religious names often reflect the beliefs and hopes of families for their children, connecting new generations to ancient traditions and spiritual heritage.
                </p>
                <p>
                  Names help us identify and distinguish people, places, and things from one another, often reflecting the values, traditions, and beliefs of those who use them. They can significantly impact a person's self-identity and sense of belonging, providing a connection to family, community, and cultural heritage.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center text-slate-600 pt-8 pb-4">
          <p>© 2025 namedByGod | Discovering the Divine Purpose in Names</p>
        </div>
      </div>
    </div>
  );
};

export default NamedByGod;